import { create } from 'zustand';
import type {
    Stone,
    StoneColor,
    StoneShape,
    StoneType,
    CellData,
    CellPos,
    LevelConfig,
    AttrEntry,
} from '../types';
import { getShapeCells, getShapeAnchorOffset } from '../data/shapes';
import { generateRandomAttrs, generateCraftSkill } from '../data/attributes';
import { autoFill } from '../utils/autoFill';

/* ===== 棋盘尺寸 ===== */
export const BOARD_ROWS = 7;
export const BOARD_COLS = 6;

/* ===== 生成关卡解锁格子配置 ===== */
function generateLevelConfigs(): LevelConfig[] {
    // 棋盘总格子: 第0行(1×4) + 第1-6行(6×6) = 4 + 36 = 40
    //
    // 解锁顺序（用户指定）：
    //   Level 1: 4×4 = 16格 (rows 1-4, cols 1-4)
    //   Level 2: 左侧竖条4格 (col 0, rows 1-4)
    //   Level 3: 右侧竖条4格 (col 5, rows 1-4)
    //   Level 4: 下方靠左横向4格 (row 5, cols 0-3)
    //   Level 5: 4级下方4格 (row 6, cols 0-3)
    //   Level 6: 4,5级右侧4格 (rows 5-6, cols 4-5)
    //   Level 7: 最顶上4格长条 (row 0, cols 1-4)
    //
    // 视觉布局：
    //   Row 0: [  ][T7][T7][T7][T7][  ]   ← level 7
    //   Row 1: [  ][L1][L1][L1][L1][  ]   ← level 1
    //   Row 2: [L2][L1][L1][L1][L1][L3]   ← level 1+2+3
    //   Row 3: [L2][L1][L1][L1][L1][L3]
    //   Row 4: [L2][L1][L1][L1][L1][L3]   ← level 1+2+3
    //   Row 5: [L4][L4][L4][L4][L6][L6]   ← level 4+6
    //   Row 6: [L5][L5][L5][L5][L6][L6]   ← level 5+6

    const levelCells: CellPos[][] = [
        // Level 1: 4×4 (rows 1-4, cols 1-4)
        (() => {
            const cells: CellPos[] = [];
            for (let r = 1; r <= 4; r++)
                for (let c = 1; c <= 4; c++)
                    cells.push({ row: r, col: c });
            return cells;
        })(),
        // Level 2: 左侧竖条 (col 0, rows 1-4)
        (() => {
            const cells: CellPos[] = [];
            for (let r = 1; r <= 4; r++) cells.push({ row: r, col: 0 });
            return cells;
        })(),
        // Level 3: 右侧竖条 (col 5, rows 1-4)
        (() => {
            const cells: CellPos[] = [];
            for (let r = 1; r <= 4; r++) cells.push({ row: r, col: 5 });
            return cells;
        })(),
        // Level 4: 下方靠左横向4格 (row 5, cols 0-3)
        (() => {
            const cells: CellPos[] = [];
            for (let c = 0; c <= 3; c++) cells.push({ row: 5, col: c });
            return cells;
        })(),
        // Level 5: 4级下方4格 (row 6, cols 0-3)
        (() => {
            const cells: CellPos[] = [];
            for (let c = 0; c <= 3; c++) cells.push({ row: 6, col: c });
            return cells;
        })(),
        // Level 6: 4,5级右侧4格 (rows 5-6, cols 4-5)
        (() => {
            const cells: CellPos[] = [];
            for (let r = 5; r <= 6; r++)
                for (let c = 4; c <= 5; c++)
                    cells.push({ row: r, col: c });
            return cells;
        })(),
        // Level 7: 最顶上4格长条 (row 0, cols 1-4)
        (() => {
            const cells: CellPos[] = [];
            for (let c = 1; c <= 4; c++) cells.push({ row: 0, col: c });
            return cells;
        })(),
    ];

    // 构建累积解锁列表
    const configs: LevelConfig[] = [];
    let cumulative: CellPos[] = [];
    for (let level = 1; level <= 7; level++) {
        cumulative = [...cumulative, ...levelCells[level - 1]];
        configs.push({
            level,
            totalCells: cumulative.length,
            unlockedCells: [...cumulative],
        });
    }

    return configs;
}

export const LEVEL_CONFIGS = generateLevelConfigs();

/* ===== 创建空棋盘 ===== */
function createEmptyBoard(): CellData[][] {
    const board: CellData[][] = [];
    for (let r = 0; r < BOARD_ROWS; r++) {
        board[r] = [];
        for (let c = 0; c < BOARD_COLS; c++) {
            board[r][c] = {
                active: false,
                occupied: false,
                stoneId: null,
            };
        }
    }
    return board;
}

/* ===== 应用关卡解锁 ===== */
function applyLevel(board: CellData[][], level: number): CellData[][] {
    const config = LEVEL_CONFIGS[level - 1];
    const newBoard = board.map(row => row.map(cell => ({ ...cell })));
    for (const pos of config.unlockedCells) {
        newBoard[pos.row][pos.col] = {
            ...newBoard[pos.row][pos.col],
            active: true,
        };
    }
    return newBoard;
}

/* ===== 石头ID生成 ===== */
let stoneIdCounter = 0;
function nextStoneId(): string {
    return `stone_${++stoneIdCounter}`;
}

/* ===== 游戏状态 ===== */
interface GameState {
    // 棋盘
    board: CellData[][];
    // 当前等级
    level: number;
    // 拥有的石头（库存）
    stones: Stone[];
    // 棋盘上放置的石头（key: stoneId, value: 放置的锚点坐标）
    placedStones: Record<string, CellPos>;
    // 当前选中的石头
    selectedStoneId: string | null;
    // 总计属性
    totalAttrs: AttrEntry[];
    // 当前拖拽的石头ID
    draggingStoneId: string | null;

    // 操作
    setLevel: (level: number) => void;
    addStone: (type: StoneType, shape: StoneShape, color: StoneColor, element: ElementType, attributes?: AttrEntry[]) => void;
    removeStone: (stoneId: string) => void;
    selectStone: (stoneId: string | null) => void;
    rotateStone: (stoneId: string) => void;
    placeStone: (stoneId: string, anchorRow: number, anchorCol: number) => boolean;
    removePlacedStone: (stoneId: string) => void;
    setDraggingStone: (stoneId: string | null) => void;
    resetBoard: () => void;
    autoFillBoard: () => boolean;
    recalcAttributes: () => void;
    canPlaceStone: (stoneId: string, anchorRow: number, anchorCol: number) => boolean;
    getStone: (stoneId: string) => Stone | undefined;
}

export const useGameStore = create<GameState>((set, get) => {
    const initialBoard = applyLevel(createEmptyBoard(), 1);

    return {
        board: initialBoard,
        level: 1,
        stones: [],
        placedStones: {},
        selectedStoneId: null,
        totalAttrs: [],
        draggingStoneId: null,

        setLevel: (level) => {
            const state = get();
            // 清除所有放置的石头
            let newBoard = createEmptyBoard();
            newBoard = applyLevel(newBoard, level);
            set({
                level,
                board: newBoard,
                placedStones: {},
                selectedStoneId: null,
            });
            get().recalcAttributes();
        },

        addStone: (type, shape, color, element, attributes) => {
            const state = get();
            let attrs: AttrEntry[];

            if (type === 'craft') {
                // 匠心石：只有1个灵蕴技能属性，颜色不影响属性数量
                // 如果传入了属性则使用传入的，否则用前缀元素生成灵蕴技
                if (attributes && attributes.length > 0) {
                    attrs = attributes;
                } else {
                    attrs = [generateCraftSkill(element)];
                }
            } else {
                // 机巧石：按颜色规则生成属性，使用前缀元素筛选
                attrs = attributes || generateRandomAttrs(color, element);
            }
            const stone: Stone = {
                id: nextStoneId(),
                type,
                shape,
                color,
                element,
                attributes: attrs,
                currentRotation: 0,
            };
            set({ stones: [...state.stones, stone] });
        },

        removeStone: (stoneId) => {
            const state = get();
            // 如果石头已放置，先移除
            const newPlaced = { ...state.placedStones };
            if (newPlaced[stoneId]) {
                delete newPlaced[stoneId];
            }
            // 清除棋盘上的占用
            const newBoard = state.board.map(row => row.map(cell => {
                if (cell.stoneId === stoneId) {
                    return { ...cell, occupied: false, stoneId: null };
                }
                return { ...cell };
            }));
            set({
                stones: state.stones.filter(s => s.id !== stoneId),
                placedStones: newPlaced,
                board: newBoard,
                selectedStoneId: state.selectedStoneId === stoneId ? null : state.selectedStoneId,
            });
            get().recalcAttributes();
        },

        selectStone: (stoneId) => {
            set({ selectedStoneId: stoneId });
        },

        rotateStone: (stoneId) => {
            const state = get();
            const stone = state.stones.find(s => s.id === stoneId);
            if (!stone) return;

            const updatedStones = state.stones.map(s => {
                if (s.id === stoneId) {
                    return { ...s, currentRotation: (s.currentRotation + 1) % 4 };
                }
                return s;
            });

            // 如果石头已放置，需要重新验证位置（保持"形状第一个实际格子"不动）
            if (state.placedStones[stoneId]) {
                const anchor = state.placedStones[stoneId]; // 已是"形状第一个实际格子"坐标
                const newStone = updatedStones.find(s => s.id === stoneId)!;
                const cells = getShapeCells(newStone.shape, newStone.currentRotation);
                const newOffset = getShapeAnchorOffset(newStone.shape, newStone.currentRotation);
                const newBoard = state.board.map(row => row.map(cell => ({ ...cell })));

                // 清除旧位置
                for (let r = 0; r < BOARD_ROWS; r++) {
                    for (let c = 0; c < BOARD_COLS; c++) {
                        if (newBoard[r][c].stoneId === stoneId) {
                            newBoard[r][c] = { ...newBoard[r][c], occupied: false, stoneId: null };
                        }
                    }
                }

                // 检查新位置是否有效：以 anchor 为"形状第一个实际格子"，其他格子由 cells+offset 推算
                let valid = true;
                for (const [dr, dc] of cells) {
                    const nr = anchor.row + dr - newOffset.dr;
                    const nc = anchor.col + dc - newOffset.dc;
                    if (nr < 0 || nr >= BOARD_ROWS || nc < 0 || nc >= BOARD_COLS) {
                        valid = false;
                        break;
                    }
                    if (!newBoard[nr][nc].active) {
                        valid = false;
                        break;
                    }
                    if (newBoard[nr][nc].occupied && newBoard[nr][nc].stoneId !== stoneId) {
                        valid = false;
                        break;
                    }
                }

                if (valid) {
                    for (const [dr, dc] of cells) {
                        const nr = anchor.row + dr - newOffset.dr;
                        const nc = anchor.col + dc - newOffset.dc;
                        newBoard[nr][nc] = { ...newBoard[nr][nc], occupied: true, stoneId };
                    }
                    set({ stones: updatedStones, board: newBoard });
                } else {
                    // 旋转后位置无效，移除放置
                    const newPlaced = { ...state.placedStones };
                    delete newPlaced[stoneId];
                    set({ stones: updatedStones, board: newBoard, placedStones: newPlaced });
                }
            } else {
                set({ stones: updatedStones });
            }
            get().recalcAttributes();
        },

        canPlaceStone: (stoneId, anchorRow, anchorCol) => {
            const state = get();
            const stone = state.stones.find(s => s.id === stoneId);
            if (!stone) return false;

            // 调用方传入的 (anchorRow, anchorCol) 已是"形状第一个实际格子"在棋盘上的坐标
            // 不需要再做偏移修正，直接用 getShapeCells 推算其他格子
            const offset = getShapeAnchorOffset(stone.shape, stone.currentRotation);
            const cells = getShapeCells(stone.shape, stone.currentRotation);
            for (const [dr, dc] of cells) {
                const nr = anchorRow + dr - offset.dr;
                const nc = anchorCol + dc - offset.dc;
                if (nr < 0 || nr >= BOARD_ROWS || nc < 0 || nc >= BOARD_COLS) return false;
                if (!state.board[nr][nc].active) return false;
                if (state.board[nr][nc].occupied && state.board[nr][nc].stoneId !== stoneId) return false;
            }
            return true;
        },

        placeStone: (stoneId, anchorRow, anchorCol) => {
            const state = get();
            if (!state.canPlaceStone(stoneId, anchorRow, anchorCol)) return false;

            const stone = state.stones.find(s => s.id === stoneId);
            if (!stone) return false;

            // 调用方传入的 (anchorRow, anchorCol) 已是"形状第一个实际格子"在棋盘上的坐标
            const offset = getShapeAnchorOffset(stone.shape, stone.currentRotation);
            const cells = getShapeCells(stone.shape, stone.currentRotation);
            const newBoard = state.board.map(row => row.map(cell => ({ ...cell })));

            // 如果石头已放置在其他位置，先清除
            if (state.placedStones[stoneId]) {
                for (let r = 0; r < BOARD_ROWS; r++) {
                    for (let c = 0; c < BOARD_COLS; c++) {
                        if (newBoard[r][c].stoneId === stoneId) {
                            newBoard[r][c] = { ...newBoard[r][c], occupied: false, stoneId: null };
                        }
                    }
                }
            }

            // 放置到新位置：以传入坐标为"形状第一个实际格子"，其他格子由 cells 推算
            const placedCells: { row: number; col: number }[] = [];
            for (const [dr, dc] of cells) {
                const nr = anchorRow + dr - offset.dr;
                const nc = anchorCol + dc - offset.dc;
                newBoard[nr][nc] = { ...newBoard[nr][nc], occupied: true, stoneId };
                placedCells.push({ row: nr, col: nc });
            }

            // 记录的锚点改为"形状第一个实际格子"的坐标，与渲染层保持一致
            const firstCell = placedCells[0];
            set({
                board: newBoard,
                placedStones: { ...state.placedStones, [stoneId]: { row: firstCell.row, col: firstCell.col } },
            });
            get().recalcAttributes();
            return true;
        },

        removePlacedStone: (stoneId) => {
            const state = get();
            if (!state.placedStones[stoneId]) return;

            const newPlaced = { ...state.placedStones };
            delete newPlaced[stoneId];
            const newBoard = state.board.map(row => row.map(cell => {
                if (cell.stoneId === stoneId) {
                    return { ...cell, occupied: false, stoneId: null };
                }
                return { ...cell };
            }));

            set({ board: newBoard, placedStones: newPlaced });
            get().recalcAttributes();
        },

        setDraggingStone: (stoneId) => {
            set({ draggingStoneId: stoneId });
        },

        resetBoard: () => {
            const state = get();
            let newBoard = createEmptyBoard();
            newBoard = applyLevel(newBoard, state.level);
            set({
                board: newBoard,
                placedStones: {},
                selectedStoneId: null,
            });
            get().recalcAttributes();
        },

        autoFillBoard: () => {
            const state = get();
            // 获取所有已录入的石头
            const stones = state.stones;
            if (stones.length === 0) return false;

            // 获取当前激活的格子
            const activeCells: CellPos[] = [];
            for (let r = 0; r < BOARD_ROWS; r++) {
                for (let c = 0; c < BOARD_COLS; c++) {
                    if (state.board[r][c].active) {
                        activeCells.push({ row: r, col: c });
                    }
                }
            }

            const result = autoFill(stones, activeCells, BOARD_ROWS, BOARD_COLS);
            if (!result) return false;

            // 应用结果
            let newBoard = createEmptyBoard();
            newBoard = applyLevel(newBoard, state.level);
            const newPlaced: Record<string, CellPos> = {};

            for (const { stoneId, anchorRow, anchorCol } of result.placements) {
                const stone = stones.find(s => s.id === stoneId);
                if (!stone) continue;
                const cells = getShapeCells(stone.shape, stone.currentRotation);
                const offset = getShapeAnchorOffset(stone.shape, stone.currentRotation);
                // autoFill 返回的锚点是矩阵左上角，转为"形状第一个实际格子"坐标存储
                const firstRow = anchorRow + offset.dr;
                const firstCol = anchorCol + offset.dc;
                for (const [dr, dc] of cells) {
                    const nr = anchorRow + dr;
                    const nc = anchorCol + dc;
                    newBoard[nr][nc] = { ...newBoard[nr][nc], occupied: true, stoneId };
                }
                newPlaced[stoneId] = { row: firstRow, col: firstCol };
            }

            set({ board: newBoard, placedStones: newPlaced });
            get().recalcAttributes();
            return true;
        },

        recalcAttributes: () => {
            const state = get();
            const attrMap = new Map<string, AttrEntry>();

            for (const stoneId of Object.keys(state.placedStones)) {
                const stone = state.stones.find(s => s.id === stoneId);
                if (!stone) continue;
                for (const attr of stone.attributes) {
                    const existing = attrMap.get(attr.id);
                    if (existing) {
                        attrMap.set(attr.id, { ...existing, value: existing.value + attr.value });
                    } else {
                        attrMap.set(attr.id, { ...attr });
                    }
                }
            }

            set({ totalAttrs: Array.from(attrMap.values()) });
        },

        getStone: (stoneId) => {
            return get().stones.find(s => s.id === stoneId);
        },
    };
});